import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Paper,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { WidgetConfig } from '../types';
import { Vehicle } from './MilesCarousel';

interface WelcomeWebAppUserScreenProps {
  onSendMessage: (message: string) => void;
  isSending: boolean;
  dealerName?: string;
  statusMessage?: string;
  config: WidgetConfig;
  onEnhanceClick: (currentVehicle?: Vehicle) => void;
}

const WelcomeWebAppUserScreen: React.FC<WelcomeWebAppUserScreenProps> = ({
  onSendMessage,
  isSending,
  dealerName = 'your agent',
  statusMessage = 'Online',
  config,
  onEnhanceClick,
}) => {
  const [inputValue, setInputValue] = useState('');

  const quickActions = [
    {
      id: 'is-property-available',
      text: 'Is This Property Available?',
      message: 'Is this property still available?',
      icon: '🏠',
    },
    {
      id: 'trade-value',
      text: 'Request Valuation',
      message: 'I want to request a property valuation.',
      icon: '💰',
    },
    {
      id: 'test-drive',
      text: 'Book a Showing',
      message: 'Book a showing',
      icon: '📅',
    },
    {
      id: 'confirm-today-drive',
      text: 'Confirm Price',
      message: 'Confirm the current price',
      icon: '🏷️',
    },
    {
      id: 'vehicle-history',
      text: 'Property History',
      message: 'Property history',
      icon: '📋',
    },
    {
      id: 'share-property',
      text: 'Share This Property',
      message: 'Share this property',
      icon: '🔗',
    },
    {
      id: 'virtual-walkaround',
      text: 'Virtual Tour',
      message: 'Virtual tour',
      icon: '🎥',
    },
    {
      id: 'payments',
      text: 'Payment Calculator',
      message: 'Payment calculator',
      icon: '🧮',
    },
   
  ];

  const handleQuickAction = (message: string) => {
    // onSendMessage(message);
    setInputValue(message);
  };

  const handleSend = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: `url('https://miles-cdn.qrbuddy.ai/static/img_miles_widget_bg.png')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        p: 3,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
        },
      }}
    >
      {/* Input Field at Bottom */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder='Ask me anything'
          disabled={isSending}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isSending}
                  edge='end'
                  sx={{ color: 'primary.main', transform: 'rotate(315deg)', }}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              bgcolor: 'white',
              borderRadius: 3,
            },
          }}
        />        
      </Box>

      {/* Title and Quick Actions First */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            mb: 2,
            fontWeight: 500,
          }}
        >
          Not sure what to ask? Start here.
        </Typography>

        <Stack spacing={2}>
          {quickActions.map((item, index) => (
            <Paper
              key={item.id}
              elevation={3}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: 3,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '50%',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 6,
                },
                ...(index % 2 === 0
                  ? { alignSelf: 'flex-start', maxWidth: '85%' }
                  : { alignSelf: 'flex-end', maxWidth: '85%' }),
              }}
              onClick={() => handleQuickAction(item.message)}
            >
              <Typography sx={{ fontSize: 28, lineHeight: 1 }}>{item.icon}</Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: '#333',
                  flex: 1,
                }}
              >
                {item.text}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>

      
    </Box>
  );
};

export default WelcomeWebAppUserScreen;
